import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, type ModalProps, Text } from "@bloomberg/media.atomics";
import type { TickerPosition } from "@bloomberg/octagon-shared";

import TickerHoldingsCard from "./TickerHoldingsCard";
import type { HoldingRowState } from "./HoldingsRow";
import WatchlistMetadata from "./WatchlistMetadata";
import styles from "./styles.module.css";

type OpenChangeEventDetails = Parameters<
  NonNullable<ModalProps["onOpenChange"]>
>[1];

export type BulkEditMetadata = {
  readonly name: string;
  readonly currency: string;
  readonly isDefault: boolean;
  readonly holdings: Readonly<Record<string, ReadonlyArray<HoldingRowState>>>;
  readonly deletedTickers: ReadonlySet<string>;
};

type BulkEditModalProps = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onSave: (metadata: BulkEditMetadata) => Promise<void>;
  readonly onDelete: () => Promise<void>;
  readonly initialName: string;
  readonly initialCurrency: string;
  readonly initialIsDefault: boolean;
  readonly positions: ReadonlyArray<TickerPosition>;
};

export default function BulkEditModal({
  open,
  onClose,
  onSave,
  onDelete,
  initialName,
  initialCurrency,
  initialIsDefault,
  positions,
}: BulkEditModalProps) {
  const [name, setName] = useState(initialName);
  const [currency, setCurrency] = useState(initialCurrency);
  const [isDefault, setIsDefault] = useState(initialIsDefault);
  const [holdingsMap, setHoldingsMap] = useState<
    Record<string, ReadonlyArray<HoldingRowState>>
  >({});
  const [snapshotPositions, setSnapshotPositions] =
    useState<ReadonlyArray<TickerPosition>>(positions);
  const [deletedTickers, setDeletedTickers] = useState<Set<string>>(
    () => new Set()
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const isBusy = isSaving || isDeleting;

  const isDirty =
    name !== initialName ||
    currency !== initialCurrency ||
    isDefault !== initialIsDefault ||
    Object.keys(holdingsMap).length > 0 ||
    deletedTickers.size > 0;

  useEffect(() => {
    if (!open) return undefined;

    const { style: bodyStyle } = document.body;
    const { style: htmlStyle } = document.documentElement;

    bodyStyle.overflow = "hidden";
    bodyStyle.position = "fixed";
    bodyStyle.inset = "0";
    htmlStyle.overflow = "hidden";

    return () => {
      bodyStyle.overflow = "";
      bodyStyle.position = "";
      bodyStyle.inset = "";
      htmlStyle.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;

    setName(initialName);
    setCurrency(initialCurrency);
    setIsDefault(initialIsDefault);
    setHoldingsMap({});
    setDeletedTickers(new Set());
    setIsSaving(false);
    setIsDeleting(false);
    setSnapshotPositions(positions);
    setShowDeleteConfirm(false);
    setShowCloseConfirm(false);
    setSaveError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = useCallback(() => {
    if (isDirty) {
      setShowCloseConfirm(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  const handleOpenChange = (
    isOpen: boolean,
    _details: OpenChangeEventDetails
  ) => {
    if (!isOpen && !isBusy) handleClose();
  };

  const handleHoldingsChange = useCallback(
    (ticker: string, rows: ReadonlyArray<HoldingRowState>) => {
      setHoldingsMap((prev) => ({ ...prev, [ticker]: rows }));
    },
    []
  );

  const handleDeleteTicker = useCallback((ticker: string) => {
    setDeletedTickers((prev) => new Set(prev).add(ticker));
  }, []);

  const visiblePositions = useMemo(
    () => snapshotPositions.filter((p) => !deletedTickers.has(p.ticker)),
    [snapshotPositions, deletedTickers]
  );

  const handleSave = useCallback(async () => {
    setSaveError(null);
    setIsSaving(true);

    try {
      await onSave({
        name,
        currency,
        isDefault,
        holdings: holdingsMap,
        deletedTickers,
      });
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Failed to save changes"
      );
    } finally {
      setIsSaving(false);
    }
  }, [onSave, name, currency, isDefault, holdingsMap, deletedTickers]);

  const handleDeleteWatchlist = useCallback(async () => {
    setSaveError(null);
    setIsDeleting(true);

    try {
      await onDelete();
    } catch (error) {
      setSaveError(
        error instanceof Error ? error.message : "Failed to delete watchlist"
      );
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  }, [onDelete]);

  return (
    <>
      <Modal
        className={styles.modal}
        isModalOpen={open && !showDeleteConfirm && !showCloseConfirm}
        onOpenChange={handleOpenChange}
        size="large"
        title="Edit Watchlist"
        subtitle="Edit your watchlist in bulk mode."
        isDismissible={false}
        footer={
          <div className={styles.footerActions}>
            <Button
              type="button"
              variant="outline"
              size="large"
              onClick={handleClose}
              disabled={isBusy}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="fill"
              size="large"
              onClick={handleSave}
              disabled={isBusy || !isDirty}
            >
              {isSaving ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        }
      >
        {saveError ? (
          <div className={styles.errorBanner} role="alert">
            {saveError}
          </div>
        ) : null}
        <WatchlistMetadata
          name={name}
          currency={currency}
          isDefault={isDefault}
          onNameChange={setName}
          onCurrencyChange={setCurrency}
          onDefaultChange={setIsDefault}
        />
        {visiblePositions.map((position) => (
          <TickerHoldingsCard
            key={position.ticker}
            ticker={position.ticker}
            companyName={
              position.marketData.longName || position.marketData.name || ""
            }
            initialLots={position.lots}
            savedRows={holdingsMap[position.ticker]}
            onChange={handleHoldingsChange}
            onDelete={() => handleDeleteTicker(position.ticker)}
          />
        ))}
        <div className={styles.deleteListSection}>
          <Button
            type="button"
            variant="outline"
            size="medium"
            className={styles.deleteWatchlistBtn}
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isBusy}
          >
            Delete Watchlist
          </Button>
        </div>
      </Modal>
      <Modal
        className={styles.confirmModal}
        isDismissible={false}
        isModalOpen={showDeleteConfirm}
        onOpenChange={(isOpen) => {
          if (!isOpen && !isDeleting) setShowDeleteConfirm(false);
        }}
        size="medium"
        title="Delete Watchlist"
        footer={
          <div className={styles.footerActions}>
            <Button
              type="button"
              variant="outline"
              size="large"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="fill"
              size="large"
              className={styles.deleteConfirmBtn}
              onClick={handleDeleteWatchlist}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </Button>
          </div>
        }
      >
        <Text asChild fontSize={14}>
          <p>
            This action cannot be undone. All positions in this watchlist will
            be permanently removed.
          </p>
        </Text>
      </Modal>
      <Modal
        className={styles.confirmModal}
        isDismissible={false}
        isModalOpen={showCloseConfirm}
        onOpenChange={(isOpen) => {
          if (!isOpen) setShowCloseConfirm(false);
        }}
        size="medium"
        title="Discard Changes"
        footer={
          <div className={styles.footerActions}>
            <Button
              type="button"
              variant="outline"
              size="large"
              onClick={() => setShowCloseConfirm(false)}
            >
              Keep Editing
            </Button>
            <Button
              type="button"
              variant="fill"
              size="large"
              className={styles.deleteConfirmBtn}
              onClick={() => {
                setShowCloseConfirm(false);
                onClose();
              }}
            >
              Discard
            </Button>
          </div>
        }
      >
        <Text asChild fontSize={14}>
          <p>
            You have unsaved changes. Are you sure you want to discard them?
          </p>
        </Text>
      </Modal>
    </>
  );
}
